import { format, isValid, intervalToDuration, parseISO, set, differenceInMinutes, differenceInHours, differenceInDays } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";

const parseMcleodDate = value => {
  if (value) {
    const formatted = value.match(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(.{5})/);
    const isoString =
      formatted[1] + "-" +
      formatted[2] + "-" +
      formatted[3] + "T" +
      formatted[4] + ":" +
      formatted[5] + ":" +
      formatted[6];

    return isoString;
  } else {
    return null;
  }
};

const parseMcleodDateWithOffset = value => {
  if (value) {
    const formatted = value.match(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(.{5})/);
    const isoString =
      formatted[1] + "-" +
      formatted[2] + "-" +
      formatted[3] + "T" +
      formatted[4] + ":" +
      formatted[5] + ":" +
      formatted[6] +
      formatted[7];

    return isoString;
  } else {
    return null;
  }
};

const formatDateForMcleod = value => {
  if (value) {
    const offsetmin = isNaN(new Date(value).getTimezoneOffset()) ? new Date().getTimezoneOffset() : new Date(value).getTimezoneOffset();
    const offsetHours = offsetmin/60;
    const mcleodOffset = offsetHours === 6 ? "-0600" : "-0500";
    const formatted = value.replace(/\D+/g, "");
    const trailingCharactersRemoved = formatted.slice(0, 14);
    const mcleodFormat = trailingCharactersRemoved + mcleodOffset;
    return mcleodFormat;
  } else {
    return undefined;
  }
};

const formatDate = (date, formatString, ifNull) => {
  if (isValid(date)) {
    return format(date, formatString);
  } else {
    return ifNull;
  }
};

const parseAndFormatDate = (date, formatString, ifNull) => {
  const parsedDate = parseISO(date);

  if (isValid(parsedDate)) {
    return format(parsedDate, formatString);
  } else {
    return ifNull;
  }
};

const getIntervals = (startDate, endDate) => {
  const abbreviations = {
    years: "yr",
    months: "mo",
    days: "d",
    hours: "h",
    minutes: "min"
  };

  let validTimes = [];
  const additionalTime = Date.parse(startDate) - Date.parse(endDate);
  const intervals = intervalToDuration({ start: 0, end: additionalTime });

  for (const [key, value] of Object.entries(intervals)) {
    value !== 0 && key !== "seconds" && validTimes.push(`${value}${abbreviations[key]}`);
  }

  return validTimes;
};

const toISOLocal = date => {
  if (date && isValid(date)) {
    const utcSeconds = new Date(date).getTime() / 1000;
    return new Date(utcSeconds * 1000 - new Date().getTimezoneOffset() * 60000).toISOString();
  }
};

const validISOFormat = date => {
  if (date) {
    const validFormat = (/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/).test(date);
    if (validFormat) return date;
    else return null;
  } else {
    return null;
  }
};

const getBeginningOfDayISOString = date => {
  return set(date, { hours: 0, minutes: 0, seconds: 0 }).toISOString();
};

const getEndOfDayISOString = date => {
  return set(date, { hours: 23, minutes: 59, seconds: 59 }).toISOString();
};

const getDifferenceInMinutes = (firstDate, secondDate) => {
  if (firstDate && secondDate) {
    let diffMs = new Date(firstDate).getTime() - new Date(secondDate).getTime();
    return (diffMs / 60000);
  } else {
    return null;
  }
};

const changeTimeZone = (date, timeZone) => {
  return utcToZonedTime(date, timeZone);
};

const timeZoneToLocal = (dateWithGMT,GMT) => {
  return new Date(new Date(dateWithGMT).toString().substring(0, 30) + GMT);
};

const getTimeZoneName = (tz) => {
  const timeZones = {
    "EDT": "America/New_York",
    "EST": "America/New_York",
    "CDT": "America/Chicago",
    "CST": "America/Chicago",
    "MDT": "America/Denver",
    "MST": "America/Phoenix",
    "PDT": "America/Los_Angeles",
    "PST": "America/Los_Angeles",
  };
  return timeZones[tz];
};

const getTimeAgo = (startDate, endDate) => {
  const diffInMinutes = differenceInMinutes(endDate, startDate);

  if (diffInMinutes < 1) {
    return "Now";
  }

  if (diffInMinutes < 60) {
    return `${diffInMinutes} min ago`;
  }

  const diffInHours = differenceInHours(endDate, startDate);

  if (diffInHours === 1) {
    return `${diffInHours} hour ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hours ago`;
  }

  const diffInDays = differenceInDays(endDate, startDate);

  if (diffInDays === 1) {
    return `${diffInDays} day ago`;
  } else {
    return `${diffInDays} days ago`;
  }
};

export {
  parseMcleodDate,
  parseMcleodDateWithOffset,
  formatDateForMcleod,
  formatDate,
  getIntervals,
  parseAndFormatDate,
  toISOLocal,
  validISOFormat,
  getBeginningOfDayISOString,
  getEndOfDayISOString,
  getDifferenceInMinutes,
  changeTimeZone,
  timeZoneToLocal,
  getTimeZoneName,
  getTimeAgo
};